const capabilities = [
  {
    icon: "ri-fire-line",
    title: "Gray Cast Iron & Ductile Iron Casting",
    subtitle: "Material Excellence",
    description:
      "Our in-house foundry operates dedicated melting furnaces for both gray cast iron (GG25/GG30) and ductile iron (GGG50/GGG70). We control every metallurgical parameter — carbon equivalent, silicon content, and inoculation — to achieve consistent mechanical properties across every production run.",
    specs: [
      "GG25 / GG30 Gray Cast Iron",
      "GGG50 / GGG70 Ductile Iron",
      "Sand Casting & Shell Mold Casting",
      "Single-piece to mass production",
    ],
    accent: "#D97706",
    bg: "rgba(217,119,6,0.05)",
    border: "rgba(217,119,6,0.2)",
    img: "https://public.readdy.ai/ai/img_res/93c7fa28c7424a7c01e6063955fd1227.jpg",
  },
  {
    icon: "ri-cpu-line",
    title: "Advanced CNC Machining Centers",
    subtitle: "Precision Engineering",
    description:
      "Post-casting, every engine block passes through our multi-axis CNC machining centers. We achieve bore tolerances within ±0.005mm, ensuring perfect cylinder geometry, deck flatness, and bearing bore alignment. Our machining lines are capable of handling both prototype runs and high-volume serial production.",
    specs: [
      "5-Axis CNC Machining Centers",
      "Bore Tolerance ±0.005mm",
      "Deck Flatness &lt;0.03mm",
      "Bearing Bore Alignment Certified",
    ],
    accent: "#92400E",
    bg: "rgba(146,64,14,0.05)",
    border: "rgba(146,64,14,0.18)",
    img: "https://readdy.ai/api/search-image?query=modern%20CNC%20machining%20center%20precision%20manufacturing%20engine%20block%20being%20machined%20clean%20industrial%20workshop%20warm%20white%20lighting%20professional%20factory%20interior%20high%20precision%20metalworking%20equipment%20warm%20tones%20industrial%20photography&width=600&height=400&seq=fm-cap-002&orientation=landscape",
  },
  {
    icon: "ri-shield-check-line",
    title: "Strict Quality Control",
    subtitle: "CMM Inspection & Flaw Detection",
    description:
      "Quality is non-negotiable. Every finished block undergoes Coordinate Measuring Machine (CMM) dimensional inspection, ultrasonic flaw detection for internal porosity, and pressure testing for coolant and oil passages. Our rejection rate is below 0.3%, and every batch ships with a full inspection report.",
    specs: [
      "CMM Dimensional Inspection",
      "Ultrasonic Flaw Detection",
      "Pressure Testing (Coolant & Oil)",
      "Rejection Rate &lt;0.3%",
    ],
    accent: "#B45309",
    bg: "rgba(180,83,9,0.05)",
    border: "rgba(180,83,9,0.18)",
    img: "https://readdy.ai/api/search-image?query=quality%20control%20CMM%20coordinate%20measuring%20machine%20inspecting%20engine%20block%20precision%20measurement%20clean%20laboratory%20environment%20warm%20white%20lighting%20professional%20industrial%20quality%20assurance%20metrology%20equipment%20warm%20tones&width=600&height=400&seq=fm-cap-003&orientation=landscape",
  },
];

export default function FMCapabilities() {
  return (
    <section
      id="fm-capabilities"
      className="py-24 px-6"
      style={{ background: "#fffbf5" }}
    >
      <div className="max-w-7xl mx-auto">
        {/* Section header */}
        <div className="text-center mb-16">
          <div
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6"
            style={{ background: "rgba(217,119,6,0.08)", border: "1px solid rgba(217,119,6,0.25)" }}
          >
            <i className="ri-tools-line text-xs" style={{ color: "#D97706" }} />
            <span
              className="text-xs font-semibold tracking-widest uppercase"
              style={{ color: "#D97706", letterSpacing: "0.2em" }}
            >
              Core Capabilities
            </span>
          </div>
          <h2
            className="font-black mb-4"
            style={{ fontSize: "clamp(2rem, 4vw, 3.2rem)", color: "#1a1a1a", letterSpacing: "-0.01em" }}
          >
            End-to-End Manufacturing
            <br />
            <span style={{ color: "#D97706" }}>Under One Roof</span>
          </h2>
          <p
            className="text-base font-light max-w-2xl mx-auto"
            style={{ color: "rgba(30,20,5,0.55)", lineHeight: "1.85" }}
          >
            From raw iron to finished, inspected engine block — our vertically integrated
            facility eliminates supply chain risk and ensures consistent quality at every stage.
          </p>
        </div>

        {/* Capability cards */}
        <div className="flex flex-col gap-8">
          {capabilities.map((cap, idx) => (
            <div
              key={cap.title}
              className={`flex flex-col ${idx % 2 === 1 ? "lg:flex-row-reverse" : "lg:flex-row"} gap-0 rounded-2xl overflow-hidden`}
              style={{ border: `1px solid ${cap.border}`, background: cap.bg }}
            >
              {/* Image */}
              <div className="lg:w-2/5 shrink-0" style={{ minHeight: "280px" }}>
                <img
                  src={cap.img}
                  alt={cap.title}
                  className="w-full h-full object-cover object-top"
                  style={{ minHeight: "280px" }}
                />
              </div>

              {/* Content */}
              <div className="flex-1 p-8 lg:p-12 flex flex-col justify-center">
                <div className="flex items-center gap-3 mb-4">
                  <div
                    className="w-10 h-10 flex items-center justify-center rounded-xl shrink-0"
                    style={{ background: `rgba(217,119,6,0.12)` }}
                  >
                    <i className={`${cap.icon} text-lg`} style={{ color: cap.accent }} />
                  </div>
                  <div>
                    <p
                      className="text-xs font-semibold tracking-widest uppercase"
                      style={{ color: cap.accent, letterSpacing: "0.18em" }}
                    >
                      {cap.subtitle}
                    </p>
                  </div>
                </div>

                <h3
                  className="font-black mb-4"
                  style={{ fontSize: "clamp(1.3rem, 2.5vw, 1.8rem)", color: "#1a1a1a", lineHeight: 1.2 }}
                >
                  {cap.title}
                </h3>

                <div
                  className="mb-5"
                  style={{ width: "40px", height: "3px", background: cap.accent, borderRadius: "2px" }}
                />

                <p
                  className="text-sm font-light leading-relaxed mb-6"
                  style={{ color: "rgba(30,20,5,0.6)", lineHeight: "1.85" }}
                >
                  {cap.description}
                </p>

                {/* Spec tags */}
                <div className="flex flex-wrap gap-2">
                  {cap.specs.map((spec) => (
                    <span
                      key={spec}
                      className="px-3 py-1.5 rounded-lg text-xs font-semibold"
                      style={{
                        background: "rgba(255,255,255,0.8)",
                        border: `1px solid ${cap.border}`,
                        color: "#1a1a1a",
                      }}
                      dangerouslySetInnerHTML={{ __html: spec }}
                    />
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
